import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { RefreshCcw, Save, Check, ChevronLeft, ChevronRight, Trash2, ShoppingCart, Loader2, Play, EyeOff, Eye } from 'lucide-react';
import ImageModal from './components/ImageModal';
import PriceDistributionView from './components/PriceDistributionView';
import {
  formatResizeDisplay,
  formatTokenCount,
  toFiniteCount,
  formatConfidencePercent,
  enrichPriceEstimateFields,
} from './utils/formatters';
import { useAuth } from './context/AuthContext';

const HISTORY_BATCH_SIZE = 80;

const getPathWithoutQuery = (url) => (url ? String(url).split('?')[0] : '');
const normalizeHistoryImageKey = (url) => getPathWithoutQuery(url).trim().toLowerCase();
const getGroupImageUrls = (group) => {
  const sourceUrls = (
    Array.isArray(group?.blob_urls) && group.blob_urls.length > 0
      ? group.blob_urls
      : [group?.blob_url]
  );
  const uniqueUrls = [];
  const seenKeys = new Set();
  sourceUrls.forEach((rawUrl) => {
    if (typeof rawUrl !== 'string') return;
    const normalizedUrl = rawUrl.trim();
    if (!normalizedUrl) return;
    const imageKey = normalizeHistoryImageKey(normalizedUrl) || normalizedUrl.toLowerCase();
    if (seenKeys.has(imageKey)) return;
    seenKeys.add(imageKey);
    uniqueUrls.push(normalizedUrl);
  });
  return uniqueUrls;
};

const canMergeIntoGroup = (group, item) => {
  if (!group || !item) return false;

  // Items belonging to the same upload share the exact same blob_url (and
  // blob_urls list) on the backend. We treat that as the strongest signal
  // for grouping and intentionally do NOT require timestamps to be within a
  // short window: a single upload can produce many rows whose timestamps
  // may drift slightly, and we never want to split one upload into multiple
  // history cards. We still require the same uploader (user_id) so that
  // distinct users who happen to reuse the same blob URL stay separated.
  if (group.user_id !== item.user_id) return false;

  const sameFirstImage = (
    !!group.blob_url &&
    !!item.blob_url &&
    getPathWithoutQuery(group.blob_url) === getPathWithoutQuery(item.blob_url)
  );
  const sameFirstImagePlural = (
    Array.isArray(group.blob_urls) &&
    Array.isArray(item.blob_urls) &&
    group.blob_urls.length > 0 &&
    item.blob_urls.length > 0 &&
    getPathWithoutQuery(group.blob_urls[0]) === getPathWithoutQuery(item.blob_urls[0])
  );
  if (!(sameFirstImage || sameFirstImagePlural)) return false;

  // As a final safety net, only merge rows that were created within a
  // generous time window (10 minutes) of each other. This prevents
  // unrelated future re-uploads of the same blob from being collapsed
  // together.
  const ts1 = new Date(group.timestamp).getTime();
  const ts2 = new Date(item.timestamp).getTime();
  if (Number.isFinite(ts1) && Number.isFinite(ts2)) {
    if (Math.abs(ts1 - ts2) >= 10 * 60 * 1000) return false;
  }
  return true;
};

const HistoryPage = () => {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [originalItemsById, setOriginalItemsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingNameId, setUpdatingNameId] = useState(null);
  const [selectedItems, setSelectedItems] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [selectedImageUrls, setSelectedImageUrls] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [ebaySearchingGroup, setEbaySearchingGroup] = useState(null);
  const [ebayResultsMap, setEbayResultsMap] = useState({});
  const [disabledImages, setDisabledImages] = useState({});
  const [rerunningGroup, setRerunningGroup] = useState(null);

  const fetchItems = async ({ reset = false } = {}) => {
    const isIncrementalLoad = !reset && Boolean(historyCursor?.id);
    if (isIncrementalLoad) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const params = { limit: HISTORY_BATCH_SIZE };
      if (!reset && historyCursor?.id) {
        params.cursor_id = historyCursor.id;
      }
      const response = await axios.get('/get_items', { params });
      const payload = response.data;
      const fetchedItems = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.items) ? payload.items : []);
      const normalizedFetchedItems = fetchedItems.map((item) => (
        item && typeof item === 'object' ? enrichPriceEstimateFields(item) : item
      ));
      const paging = Array.isArray(payload) ? null : payload?.paging;

      setItems(prevItems => {
        const existingIds = new Set((reset ? [] : prevItems).map(item => item.id));
        const dedupedIncoming = normalizedFetchedItems.filter(item => !existingIds.has(item.id));
        return reset ? dedupedIncoming : [...prevItems, ...dedupedIncoming];
      });

      setOriginalItemsById(prev => {
        const next = reset ? {} : { ...prev };
        normalizedFetchedItems.forEach(item => {
          if (!item || item.id === undefined || item.id === null) return;
          if (next[item.id] && !reset) return;
          next[item.id] = {
            actual_price: item.actual_price,
            manual_name: item.manual_name || '',
          };
        });
        return next;
      });

      setHasMoreHistory(Boolean(paging?.has_more));
      setHistoryCursor(paging?.next_cursor || null);

      if (reset) {
        setCurrentPage(1);
        setDisabledImages({});
        setEbayResultsMap({});
      }
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      if (isIncrementalLoad) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const refreshHistory = async () => {
    await fetchItems({ reset: true });
  };

  const flushHistory = async () => {
    if (!window.confirm("Are you sure you want to clear all analysis history and blob storage? This cannot be undone.")) return;
    
    try {
      const response = await axios.post('/flush_history');
      if (response.data.status === 'success') {
        setItems([]);
        setOriginalItemsById({});
        setHasMoreHistory(false);
        setHistoryCursor(null);
        setDisabledImages({});
        setEbayResultsMap({});
        setCurrentPage(1);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error("Error flushing history:", error);
      alert("Failed to flush history.");
    }
  };

  useEffect(() => {
    fetchItems({ reset: true });
  }, []);

  const getGroupKey = (group) => `${group.blob_url}-${group.timestamp}`;

  const toggleImage = (groupKey, imageUrl, allUrls = []) => {
    setDisabledImages(prev => {
      const imageKey = normalizeHistoryImageKey(imageUrl);
      if (!imageKey) return prev;

      const allKeys = Array.from(new Set(
        (Array.isArray(allUrls) ? allUrls : [])
          .map((url) => normalizeHistoryImageKey(url))
          .filter(Boolean)
      ));
      const groupDisabled = new Set(
        (Array.isArray(prev[groupKey]) ? prev[groupKey] : [])
          .map((key) => normalizeHistoryImageKey(key))
          .filter(Boolean)
      );
      const isCurrentlyDisabled = groupDisabled.has(imageKey);

      if (isCurrentlyDisabled) {
        groupDisabled.delete(imageKey);
      } else {
        const enabledKeys = allKeys.filter((key) => !groupDisabled.has(key));
        const disablingLastEnabledImage = enabledKeys.length <= 1 && enabledKeys.includes(imageKey);
        if (disablingLastEnabledImage) {
          return prev;
        }
        groupDisabled.add(imageKey);
      }
      return { ...prev, [groupKey]: Array.from(groupDisabled) };
    });
  };

  const getEnabledUrls = (group) => {
    const groupKey = getGroupKey(group);
    const disabled = new Set(
      (Array.isArray(disabledImages[groupKey]) ? disabledImages[groupKey] : [])
        .map((key) => normalizeHistoryImageKey(key))
        .filter(Boolean)
    );
    const allUrls = getGroupImageUrls(group);
    const enabledUrls = [];
    const seenKeys = new Set();
    allUrls.forEach((url) => {
      const key = normalizeHistoryImageKey(url);
      if (!key || seenKeys.has(key) || disabled.has(key)) return;
      seenKeys.add(key);
      enabledUrls.push(url);
    });
    return enabledUrls;
  };

  const rerunGroup = async (group) => {
    const enabledUrls = getEnabledUrls(group);
    if (enabledUrls.length === 0) {
      alert('At least one image must be enabled to re-run analysis.');
      return;
    }
    const groupKey = getGroupKey(group);
    setRerunningGroup(groupKey);
    try {
      const response = await axios.post('/analyze', {
        image_urls: enabledUrls,
        resize_mode: group.resize_mode || 'original',
        compression_mode: group.compression_mode || 'none',
        model: group.model_name,
      });
      if (response.data.status === 'success') {
        await refreshHistory();
      } else {
        alert(response.data.message || 'Re-run failed.');
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.response?.data?.message || error.message || 'Re-run failed.';
      alert(`Error re-running analysis: ${msg}`);
    } finally {
      setRerunningGroup(null);
    }
  };

  const searchEbayForGroup = async (group) => {
    const groupKey = `${group.blob_url}-${group.timestamp}`;
    setEbaySearchingGroup(groupKey);
    try {
      const itemIds = group.items.filter(i => i.id).map(i => i.id);
      const response = await axios.post('/ebay_search', {
        blob_urls: group.blob_urls || [group.blob_url],
        item_ids: itemIds
      });
      if (response.data.status === 'success') {
        setEbayResultsMap(prev => ({ ...prev, [groupKey]: response.data.ebay_results }));
        // Update items state so ebay_results persist in local data
        const ebayJson = response.data.ebay_results;
        setItems(prev => prev.map(item => 
          itemIds.includes(item.id) ? { ...item, ebay_results: ebayJson } : item
        ));
      }
    } catch (error) {
      console.error('Error searching eBay:', error);
    } finally {
      setEbaySearchingGroup(null);
    }
  };

  const handlePriceChange = (id, value) => {
    setItems(prevItems => prevItems.map(item =>
      item.id === id ? { ...item, actual_price: value } : item
    ));
  };

  const handleManualNameChange = (id, value) => {
    setItems(prevItems => prevItems.map(item =>
      item.id === id ? { ...item, manual_name: value } : item
    ));
  };

  const updateItemName = async (id, name) => {
    setUpdatingNameId(id);
    try {
      const response = await axios.post('/update_item_name', {
        id: id,
        item_name: name
      });
      if (response.data.status === 'success') {
        setOriginalItemsById(prev => ({
          ...prev,
          [id]: {
            ...(prev[id] || {}),
            manual_name: name,
          },
        }));
      } else {
        alert("Error: " + response.data.message);
      }
    } catch (error) {
      console.error("Error updating item name:", error);
      alert("Error updating item name. Please try again.");
    } finally {
      setUpdatingNameId(null);
    }
  };

  const updatePrice = async (id, price) => {
    setUpdatingId(id);
    try {
      const response = await axios.post('/update_price', {
        id: id,
        actual_price: parseFloat(price)
      });
      if (response.data.status === 'success') {
        const parsedPrice = parseFloat(price);
        setOriginalItemsById(prev => ({
          ...prev,
          [id]: {
            ...(prev[id] || {}),
            actual_price: Number.isFinite(parsedPrice) ? parsedPrice : null,
          },
        }));
      } else {
        alert("Error: " + response.data.message);
      }
    } catch (error) {
      console.error("Error updating price:", error);
      alert("Error updating price. Please try again.");
    } finally {
      setUpdatingId(null);
    }
  };

  const groupItems = (rows) => {
    const groups = [];
    rows.forEach(item => {
      const previousGroup = groups.length > 0 ? groups[groups.length - 1] : null;
      if (previousGroup && canMergeIntoGroup(previousGroup, item)) {
        previousGroup.items.push(item);
        if ((!previousGroup.resize_mode || previousGroup.resize_mode === 'N/A') && item.resize_mode && item.resize_mode !== 'N/A') {
          previousGroup.resize_mode = item.resize_mode;
        }
        if ((!previousGroup.compression_mode || previousGroup.compression_mode === 'N/A') && item.compression_mode && item.compression_mode !== 'N/A') {
          previousGroup.compression_mode = item.compression_mode;
        }
        if ((!previousGroup.analysis_source || previousGroup.analysis_source === 'N/A') && item.analysis_source) {
          previousGroup.analysis_source = item.analysis_source;
        }
        if (previousGroup.cache_hit === undefined || previousGroup.cache_hit === null) {
          previousGroup.cache_hit = item.cache_hit;
        }
        if (previousGroup.use_ebay_rag === undefined || previousGroup.use_ebay_rag === null) {
          previousGroup.use_ebay_rag = item.use_ebay_rag;
        }
        if (previousGroup.ebay_rag_result_count === undefined || previousGroup.ebay_rag_result_count === null) {
          previousGroup.ebay_rag_result_count = item.ebay_rag_result_count;
        }
        if (previousGroup.use_notes_rag === undefined || previousGroup.use_notes_rag === null) {
          previousGroup.use_notes_rag = item.use_notes_rag;
        }
        if (previousGroup.assume_hidden_items === undefined || previousGroup.assume_hidden_items === null) {
          previousGroup.assume_hidden_items = item.assume_hidden_items;
        }
        if (previousGroup.notes_rag_example_count === undefined || previousGroup.notes_rag_example_count === null) {
          previousGroup.notes_rag_example_count = item.notes_rag_example_count;
        }
        if (!previousGroup.usage && item.usage) {
          previousGroup.usage = item.usage;
        }
        if (previousGroup.input_tokens_consumed === undefined || previousGroup.input_tokens_consumed === null) {
          previousGroup.input_tokens_consumed = item.input_tokens_consumed;
        }
        if (previousGroup.output_tokens_consumed === undefined || previousGroup.output_tokens_consumed === null) {
          previousGroup.output_tokens_consumed = item.output_tokens_consumed;
        }
        if (previousGroup.total_tokens_consumed === undefined || previousGroup.total_tokens_consumed === null) {
          previousGroup.total_tokens_consumed = item.total_tokens_consumed;
        }
      } else {
        groups.push({
          blob_url: item.blob_url,
          blob_urls: item.blob_urls,
          timestamp: item.timestamp,
          username: item.username,
          user_id: item.user_id,
          user_deleted: item.user_deleted,
          model_name: item.model_name,
          resize_mode: item.resize_mode,
          compression_mode: item.compression_mode,
          analysis_duration_ms: item.analysis_duration_ms,
          analysis_cost: item.analysis_cost,
          analysis_cost_display: item.analysis_cost_display,
          analysis_source: item.analysis_source,
          cache_hit: item.cache_hit,
          use_ebay_rag: item.use_ebay_rag,
          ebay_rag_result_count: item.ebay_rag_result_count,
          use_notes_rag: item.use_notes_rag,
          assume_hidden_items: item.assume_hidden_items,
          notes_rag_example_count: item.notes_rag_example_count,
          usage: item.usage,
          input_tokens_consumed: item.input_tokens_consumed,
          output_tokens_consumed: item.output_tokens_consumed,
          total_tokens_consumed: item.total_tokens_consumed,
          items: [item]
        });
      }
    });
    return groups;
  };

  const groupedItems = useMemo(() => groupItems(items), [items]);
  
  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(groupedItems.length / itemsPerPage));
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGroups = groupedItems.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    const nextPage = Math.max(1, Math.min(totalPages, pageNumber));
    setCurrentPage(nextPage);
  };
  const canGoNext = currentPage < totalPages || hasMoreHistory;
  const handleNextPage = async () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      return;
    }
    if (!hasMoreHistory || loadingMore) {
      return;
    }
    await fetchItems({ reset: false });
    setCurrentPage(prev => prev + 1);
  };

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 bg-white rounded-lg shadow-md">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-8 gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Auction Item History</h1>
        <div className="flex gap-2 sm:gap-4">
          <button 
            onClick={refreshHistory}
            className="flex items-center px-3 sm:px-4 py-2 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors text-sm sm:text-base"
          >
            <RefreshCcw size={16} className={`mr-1.5 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button 
            onClick={flushHistory}
            disabled={items.length === 0}
            className={`flex items-center px-3 sm:px-4 py-2 rounded text-white transition-colors text-sm sm:text-base ${
              items.length === 0 ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
            }`}
            title="Clear all analysis history"
          >
            <Trash2 size={16} className="mr-1.5 sm:mr-2" />
            Flush
          </button>
        </div>
      </div>

      <div className="space-y-4 sm:space-y-8">
        {currentGroups.map((group, gIdx) => (
          <div key={`${group.blob_url}-${group.timestamp}`} className="border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            {(() => {
              const gKey = getGroupKey(group);
              const allUrls = getGroupImageUrls(group);
              const groupDisabled = new Set(
                (Array.isArray(disabledImages[gKey]) ? disabledImages[gKey] : [])
                  .map((key) => normalizeHistoryImageKey(key))
                  .filter(Boolean)
              );
              const enabledKeySet = new Set(
                allUrls
                  .map((url) => normalizeHistoryImageKey(url))
                  .filter((key) => key && !groupDisabled.has(key))
              );
              const enabledCount = enabledKeySet.size;
              const hasDisabled = groupDisabled.size > 0;
              const isRerunning = rerunningGroup === gKey;
              const representativeItem = group.items?.[0] || {};
              const usage = (group.usage && typeof group.usage === 'object')
                ? group.usage
                : ((representativeItem.usage && typeof representativeItem.usage === 'object') ? representativeItem.usage : {});
              const inputTokens = toFiniteCount(
                usage.prompt_tokens ?? usage.input_tokens ?? group.input_tokens_consumed ?? representativeItem.input_tokens_consumed ?? null
              );
              const outputTokens = toFiniteCount(
                usage.completion_tokens ?? usage.output_tokens ?? group.output_tokens_consumed ?? representativeItem.output_tokens_consumed ?? null
              );
              const rawTotalTokens = toFiniteCount(
                usage.total_tokens ?? group.total_tokens_consumed ?? representativeItem.total_tokens_consumed ?? null
              );
              const totalTokens = (
                inputTokens === null && outputTokens === null
                  ? null
                  : (rawTotalTokens !== null
                    ? rawTotalTokens
                    : (inputTokens !== null && outputTokens !== null ? (inputTokens + outputTokens) : null))
              );
              const sourceRaw = String(group.analysis_source ?? representativeItem.analysis_source ?? '').trim().toLowerCase();
              const cacheHit = group.cache_hit === true || representativeItem.cache_hit === true || sourceRaw === 'cache';
              const sourceLabel = cacheHit ? 'CACHE' : 'LIVE';
              const sourceClass = cacheHit
                ? 'bg-orange-100 text-orange-700 border border-orange-200'
                : 'bg-green-100 text-green-700 border border-green-200';
              const useEbayRag = (
                group.use_ebay_rag !== undefined && group.use_ebay_rag !== null
                  ? Boolean(group.use_ebay_rag)
                  : Boolean(representativeItem.use_ebay_rag)
              );
              const ragRefs = Math.max(0, Number(group.ebay_rag_result_count ?? representativeItem.ebay_rag_result_count ?? 0) || 0);
              const useNotesRag = (
                group.use_notes_rag !== undefined && group.use_notes_rag !== null
                  ? Boolean(group.use_notes_rag)
                  : Boolean(representativeItem.use_notes_rag)
              );
              const notesRefs = Math.max(0, Number(group.notes_rag_example_count ?? representativeItem.notes_rag_example_count ?? 0) || 0);
              const assumeHiddenItems = (
                group.assume_hidden_items !== undefined && group.assume_hidden_items !== null
                  ? Boolean(group.assume_hidden_items)
                  : Boolean(representativeItem.assume_hidden_items)
              );
              return (
            <div className="bg-slate-50 p-3 sm:p-4 border-b border-slate-200">
              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                {/* Image thumbnails with enable/disable toggles */}
                <div className="flex gap-2 flex-wrap">
                  {allUrls.map((url, idx) => {
                    const imageKey = normalizeHistoryImageKey(url);
                    const isDisabled = imageKey ? groupDisabled.has(imageKey) : false;
                    const disablingLastEnabledImage = !isDisabled && enabledCount <= 1;
                    return (
                      <div key={idx} className="relative">
                        <div
                          className="cursor-pointer"
                          onClick={() => {
                            setSelectedImageUrl(url);
                            setSelectedImageUrls(allUrls);
                            setSelectedItems(group.items);
                          }}
                        >
                          <img
                            src={url}
                            alt={`Image ${idx + 1}`}
                            className={`w-14 h-14 sm:w-16 sm:h-16 object-cover rounded border-2 shadow-sm transition-all ${
                              isDisabled ? 'opacity-30 grayscale border-red-300' : 'border-white hover:scale-105'
                            }`}
                          />
                        </div>
                        {allUrls.length > 1 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleImage(gKey, url, allUrls); }}
                            disabled={disablingLastEnabledImage}
                            className={`absolute -top-1.5 -right-1.5 p-0.5 rounded-full border shadow-sm transition-colors z-10 ${
                              disablingLastEnabledImage
                                ? 'bg-slate-100 border-slate-300 text-slate-400 cursor-not-allowed'
                                : (isDisabled
                                ? 'bg-red-100 border-red-300 text-red-600 hover:bg-red-200'
                                : 'bg-green-100 border-green-300 text-green-600 hover:bg-green-200')
                            }`}
                            title={disablingLastEnabledImage ? 'At least one image must stay enabled' : (isDisabled ? 'Enable image' : 'Disable image')}
                          >
                            {isDisabled ? <EyeOff size={10} /> : <Eye size={10} />}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm font-semibold text-slate-700">{new Date(group.timestamp).toLocaleString()}</div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-[10px] font-mono text-slate-500 bg-white border px-2 py-0.5 rounded">{group.model_name}</span>
                    {group.username && (group.user_deleted || group.username !== currentUser?.username) && (
                      <span
                        className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
                          group.user_deleted
                            ? 'text-rose-700 bg-rose-50 border-rose-200'
                            : 'text-slate-700 bg-slate-100 border-slate-200'
                        }`}
                        title={group.user_deleted
                          ? `Analyzed by ${group.username} (account deleted)`
                          : `Analyzed by ${group.username}`}
                      >
                        By: {group.username}{group.user_deleted ? ' (deleted)' : ''}
                      </span>
                    )}
                    <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
                      Resize: {formatResizeDisplay(group.resize_mode)}
                    </span>
                    <span className="text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded">
                      Compression: {group.compression_mode || 'N/A'}
                    </span>
                    <span className="text-[10px] font-mono text-cyan-700 bg-cyan-50 border border-cyan-100 px-2 py-0.5 rounded">
                      eBay RAG: {useEbayRag ? `on (${ragRefs} refs)` : 'off'}
                    </span>
                    <span className="text-[10px] font-mono text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">
                      Notes RAG: {useNotesRag ? `on (${notesRefs} refs)` : 'off'}
                    </span>
                    <span className="text-[10px] font-mono text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-100 px-2 py-0.5 rounded">
                      Hidden Items: {assumeHiddenItems ? 'on' : 'off'}
                    </span>
                    <span className="text-[10px] font-mono text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">
                      {group.analysis_duration_ms !== undefined && group.analysis_duration_ms !== null 
                        ? `${Number(group.analysis_duration_ms).toFixed(0)}ms` 
                        : 'N/A'}
                    </span>
                    <span className="text-[10px] font-mono text-purple-600 bg-purple-50 border border-purple-100 px-2 py-0.5 rounded">
                      {group.analysis_cost_display !== undefined
                        ? group.analysis_cost_display
                        : (group.analysis_cost !== undefined && group.analysis_cost !== null
                          ? `$${Number(group.analysis_cost).toFixed(4)}`
                          : 'N/A')}
                    </span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${sourceClass}`}>
                      Src: {sourceLabel}
                    </span>
                    <span className="text-[10px] font-mono text-blue-700 bg-blue-100 border border-blue-200 px-2 py-0.5 rounded">
                      In Tok: {formatTokenCount(inputTokens)}
                    </span>
                    <span className="text-[10px] font-mono text-violet-700 bg-violet-100 border border-violet-200 px-2 py-0.5 rounded">
                      Out Tok: {formatTokenCount(outputTokens)}
                    </span>
                    <span className="text-[10px] font-mono text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded">
                      Tot Tok: {formatTokenCount(totalTokens)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {hasDisabled && (
                    <button
                      onClick={() => rerunGroup(group)}
                      disabled={isRerunning || enabledCount === 0}
                      className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border transition-colors ${
                        isRerunning || enabledCount === 0
                          ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                          : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'
                      }`}
                      title={`Re-run with ${enabledCount}/${allUrls.length} images`}
                    >
                      {isRerunning ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                      Re-run ({enabledCount}/{allUrls.length})
                    </button>
                  )}
                  <div className="text-right">
                    <div className="text-xs text-slate-400 uppercase tracking-wider font-bold">Items Found</div>
                    <div className="text-2xl font-bold text-slate-800">{group.items.length}</div>
                  </div>
                </div>
              </div>
            </div>
              );
            })()}

            {/* Desktop table view */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-white border-b border-slate-100">
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">AI Name</th>
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Manual Name</th>
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Est. Value</th>
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Confidence</th>
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Actual Price</th>
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Condition</th>
                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Price Source</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => {
                    const originalItem = originalItemsById[item.id];
                    const isDirty = originalItem && parseFloat(originalItem.actual_price) !== parseFloat(item.actual_price);
                    const isUpdating = updatingId === item.id;
                    const isNameDirty = originalItem && (originalItem.manual_name || '') !== (item.manual_name || '');
                    const isNameUpdating = updatingNameId === item.id;

                    return (
                      <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors last:border-0">
                        <td className="p-4 text-sm font-medium text-slate-700">
                          <button 
                            onClick={() => {
                              setSelectedImageUrl(item.blob_url);
                              setSelectedImageUrls(item.blob_urls || [item.blob_url]);
                              setSelectedItems(item);
                            }}
                            className="hover:text-blue-600 hover:underline transition-colors text-left"
                          >
                            {item.item_name}
                          </button>
                        </td>
                        <td className="p-4 text-sm">
                          <div className="flex items-center">
                            <div className="relative flex items-center">
                              <input 
                                type="text" 
                                placeholder="Custom name..."
                                value={item.manual_name || ''} 
                                onChange={(e) => handleManualNameChange(item.id, e.target.value)}
                                className={`w-36 pr-8 py-1 px-2 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                                  isNameDirty 
                                    ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' 
                                    : 'border-slate-200 focus:ring-green-500'
                                }`}
                              />
                              <button 
                                onClick={() => updateItemName(item.id, item.manual_name || '')}
                                disabled={!isNameDirty || isNameUpdating}
                                className={`absolute right-1 p-1 rounded transition-all ${
                                  isNameUpdating
                                    ? 'text-slate-400'
                                    : isNameDirty
                                      ? 'text-blue-600 hover:bg-blue-100'
                                      : 'text-green-500 cursor-default'
                                }`}
                                title={isNameDirty ? "Save Name" : "Name Saved"}
                              >
                                {isNameUpdating ? (
                                  <RefreshCcw size={14} className="animate-spin" />
                                ) : isNameDirty ? (
                                  <Save size={14} />
                                ) : (
                                  <Check size={14} />
                                )}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-right">
                          <div className="text-green-600 font-bold">${item.estimated_value?.toLocaleString()}</div>
                          <PriceDistributionView
                            rangeValue={item?.estimated_value_range}
                            distributionValue={item?.price_estimate_distribution}
                            maxBins={3}
                            compact
                            className="mt-1 text-left"
                          />
                        </td>
                        <td className="p-4 text-sm text-indigo-700 font-semibold">{formatConfidencePercent(item?.confidence_score)}</td>
                        <td className="p-4 text-sm">
                          <div className="flex items-center">
                            <div className="relative flex items-center">
                              <span className="absolute left-2 text-slate-400 text-sm">$</span>
                              <input 
                                type="number" 
                                placeholder="0.00"
                                value={item.actual_price} 
                                onChange={(e) => handlePriceChange(item.id, e.target.value)}
                                className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                                  isDirty 
                                    ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' 
                                    : 'border-slate-200 focus:ring-green-500'
                                }`}
                                step="0.01"
                              />
                              <button 
                                onClick={() => updatePrice(item.id, item.actual_price)}
                                disabled={!isDirty || isUpdating}
                                className={`absolute right-1 p-1 rounded transition-all ${
                                  isUpdating
                                    ? 'text-slate-400'
                                    : isDirty
                                      ? 'text-blue-600 hover:bg-blue-100'
                                      : 'text-green-500 cursor-default'
                                }`}
                                title={isDirty ? "Save Price" : "Price Saved"}
                              >
                                {isUpdating ? (
                                  <RefreshCcw size={14} className="animate-spin" />
                                ) : isDirty ? (
                                  <Save size={14} />
                                ) : (
                                  <Check size={14} />
                                )}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          {(() => {
                            const condition = item?.condition || 'Unknown';
                            const condBg = condition === 'New' ? 'bg-green-100 text-green-700' : condition === 'Damaged' || condition === 'For Parts' ? 'bg-red-100 text-red-700' : condition === 'Unknown' ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-700';
                            return <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${condBg}`}>{condition}</span>;
                          })()}
                        </td>
                        <td className="p-4">
                          {(() => {
                            const priceSource = item?.price_source || 'model_knowledge';
                            const sourceLabel = priceSource === 'ebay_rag' ? 'eBay RAG' : priceSource === 'image' ? 'Image' : 'Model Knowledge';
                            const sourceBg = priceSource === 'ebay_rag' ? 'bg-blue-100 text-blue-700' : priceSource === 'image' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                            return <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${sourceBg}`}>{sourceLabel}</span>;
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                  {group.items.length >= 1 && (
                    <tr className="bg-slate-50 border-t border-slate-200">
                      <td colSpan="2" className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Est. Sum</td>
                      <td className="p-4 text-sm text-green-600 font-bold text-right">${group.items.reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}</td>
                      <td colSpan="4"></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* eBay Similar Items Section */}
            {(() => {
              const groupKey = `${group.blob_url}-${group.timestamp}`;
              const ebayResults = ebayResultsMap[groupKey] || group.items[0]?.ebay_results;
              const isSearching = ebaySearchingGroup === groupKey;
              return (
                <div className="hidden sm:block border-t border-slate-100 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <ShoppingCart size={14} className="text-blue-600" />
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">eBay Similar Items</span>
                    </div>
                    <button
                      onClick={() => searchEbayForGroup(group)}
                      disabled={isSearching}
                      className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                    >
                      {isSearching ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
                      {isSearching ? 'Searching...' : ebayResults ? 'Re-search' : 'Search eBay'}
                    </button>
                  </div>
                  {isSearching && !ebayResults && (
                    <p className="text-xs text-slate-400 italic">Searching eBay for similar items...</p>
                  )}
                  {ebayResults && Array.isArray(ebayResults) && ebayResults.length === 0 && (
                    <p className="text-xs text-slate-400 italic">No similar items found.</p>
                  )}
                  {ebayResults && Array.isArray(ebayResults) && ebayResults.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {ebayResults.map((ebayItem, idx) => (
                        <a
                          key={ebayItem.itemId || idx}
                          href={ebayItem.itemWebUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 w-32 bg-white border border-slate-200 rounded overflow-hidden hover:shadow-md hover:border-blue-300 transition-all group"
                        >
                          {ebayItem.image && (
                            <div className="aspect-square bg-slate-100 overflow-hidden">
                              <img src={ebayItem.image} alt={ebayItem.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                            </div>
                          )}
                          <div className="p-1.5">
                            <p className="text-[10px] text-slate-600 line-clamp-2 mb-0.5">{ebayItem.title}</p>
                            <p className="text-xs font-bold text-green-600">${ebayItem.price}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                  {!ebayResults && !isSearching && (
                    <p className="text-xs text-slate-400 italic">Click "Search eBay" to find similar items.</p>
                  )}
                </div>
              );
            })()}

            {/* Mobile card view */}
            <div className="sm:hidden divide-y divide-slate-100">
              {group.items.map((item) => {
                const originalItem = originalItemsById[item.id];
                const isDirty = originalItem && parseFloat(originalItem.actual_price) !== parseFloat(item.actual_price);
                const isUpdating = updatingId === item.id;
                const isNameDirty = originalItem && (originalItem.manual_name || '') !== (item.manual_name || '');
                const isNameUpdating = updatingNameId === item.id;

                return (
                  <div key={item.id} className="p-3 space-y-2">
                    <button 
                      onClick={() => {
                        setSelectedImageUrl(item.blob_url);
                        setSelectedImageUrls(item.blob_urls || [item.blob_url]);
                        setSelectedItems(item);
                      }}
                      className="text-sm font-medium text-slate-700 hover:text-blue-600 hover:underline transition-colors text-left"
                    >
                      {item.item_name}
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 shrink-0">Manual:</span>
                      <div className="relative flex items-center flex-1">
                        <input 
                          type="text" 
                          placeholder="Custom name..."
                          value={item.manual_name || ''} 
                          onChange={(e) => handleManualNameChange(item.id, e.target.value)}
                          className={`w-full pr-8 py-1 px-2 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                            isNameDirty 
                              ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' 
                              : 'border-slate-200 focus:ring-green-500'
                          }`}
                        />
                        <button 
                          onClick={() => updateItemName(item.id, item.manual_name || '')}
                          disabled={!isNameDirty || isNameUpdating}
                          className={`absolute right-1 p-1 rounded transition-all ${
                            isNameUpdating
                              ? 'text-slate-400'
                              : isNameDirty
                                ? 'text-blue-600 hover:bg-blue-100'
                                : 'text-green-500 cursor-default'
                          }`}
                        >
                          {isNameUpdating ? (
                            <RefreshCcw size={14} className="animate-spin" />
                          ) : isNameDirty ? (
                            <Save size={14} />
                          ) : (
                            <Check size={14} />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-green-600 font-bold">${item.estimated_value?.toLocaleString()}</span>
                      <span className="text-xs text-indigo-700 font-semibold">Conf: {formatConfidencePercent(item?.confidence_score)}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-slate-500">Actual:</span>
                        <div className="relative flex items-center">
                          <span className="absolute left-2 text-slate-400 text-sm">$</span>
                          <input 
                            type="number" 
                            placeholder="0.00"
                            value={item.actual_price} 
                            onChange={(e) => handlePriceChange(item.id, e.target.value)}
                            className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                              isDirty 
                                ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' 
                                : 'border-slate-200 focus:ring-green-500'
                            }`}
                            step="0.01"
                          />
                          <button 
                            onClick={() => updatePrice(item.id, item.actual_price)}
                            disabled={!isDirty || isUpdating}
                            className={`absolute right-1 p-1 rounded transition-all ${
                              isUpdating
                                ? 'text-slate-400'
                                : isDirty
                                  ? 'text-blue-600 hover:bg-blue-100'
                                  : 'text-green-500 cursor-default'
                            }`}
                          >
                            {isUpdating ? (
                              <RefreshCcw size={14} className="animate-spin" />
                            ) : isDirty ? (
                              <Save size={14} />
                            ) : (
                              <Check size={14} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <PriceDistributionView
                      rangeValue={item?.estimated_value_range}
                      distributionValue={item?.price_estimate_distribution}
                      maxBins={3}
                      compact
                    />
                    <div className="flex items-center gap-2">
                      {(() => {
                        const condition = item?.condition || 'Unknown';
                        const condBg = condition === 'New' ? 'bg-green-100 text-green-700' : condition === 'Damaged' || condition === 'For Parts' ? 'bg-red-100 text-red-700' : condition === 'Unknown' ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-700';
                        const priceSource = item?.price_source || 'model_knowledge';
                        const sourceLabel = priceSource === 'ebay_rag' ? 'eBay RAG' : priceSource === 'image' ? 'Image' : 'Model Knowledge';
                        const sourceBg = priceSource === 'ebay_rag' ? 'bg-blue-100 text-blue-700' : priceSource === 'image' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                        return (
                          <>
                            <span className="text-[9px] text-slate-400">Condition:</span>
                            <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${condBg}`}>{condition}</span>
                            <span className="text-[9px] text-slate-400 ml-1">Price Src:</span>
                            <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${sourceBg}`}>{sourceLabel}</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
              {group.items.length >= 1 && (
                <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Est. Sum</span>
                  <span className="text-sm text-green-600 font-bold">${group.items.reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* eBay Similar Items - Mobile */}
            {(() => {
              const groupKey = `${group.blob_url}-${group.timestamp}`;
              const ebayResults = ebayResultsMap[groupKey] || group.items[0]?.ebay_results;
              const isSearching = ebaySearchingGroup === groupKey;
              return (
                <div className="sm:hidden border-t border-slate-100 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <ShoppingCart size={12} className="text-blue-600" />
                      <span className="text-[10px] font-bold text-slate-500 uppercase">eBay</span>
                    </div>
                    <button
                      onClick={() => searchEbayForGroup(group)}
                      disabled={isSearching}
                      className="flex items-center gap-1 text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 border border-blue-200 rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                    >
                      {isSearching ? <Loader2 size={10} className="animate-spin" /> : <RefreshCcw size={10} />}
                      {isSearching ? 'Searching...' : ebayResults ? 'Re-search' : 'Search'}
                    </button>
                  </div>
                  {ebayResults && Array.isArray(ebayResults) && ebayResults.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {ebayResults.map((ebayItem, idx) => (
                        <a
                          key={ebayItem.itemId || idx}
                          href={ebayItem.itemWebUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 w-24 bg-white border border-slate-200 rounded overflow-hidden"
                        >
                          {ebayItem.image && (
                            <img src={ebayItem.image} alt={ebayItem.title} className="w-full aspect-square object-cover" />
                          )}
                          <div className="p-1">
                            <p className="text-[9px] text-slate-600 line-clamp-1">{ebayItem.title}</p>
                            <p className="text-[10px] font-bold text-green-600">${ebayItem.price}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                  {!ebayResults && !isSearching && (
                    <p className="text-[10px] text-slate-400 italic">Tap "Search" to find similar items on eBay.</p>
                  )}
                </div>
              );
            })()}
          </div>
        ))}

        {items.length === 0 && !loading && (
          <div className="p-12 text-center text-slate-400 border-2 border-dashed rounded-lg bg-slate-50">
            <p className="italic">No analysis history found.</p>
          </div>
        )}

        {(groupedItems.length > itemsPerPage || hasMoreHistory || currentPage > 1) && (
          <div className="flex flex-col sm:flex-row justify-between items-center pt-6 border-t border-slate-100 gap-4">
            <div className="text-sm text-slate-500">
              Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, groupedItems.length)}</span> of <span className="font-medium">{groupedItems.length}{hasMoreHistory ? '+' : ''}</span> analysis sessions
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center mr-4">
                <span className="text-sm text-slate-500 mr-2">Show:</span>
                <select 
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="text-sm border border-slate-200 rounded px-2 py-1 outline-none focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <nav className="flex items-center gap-1">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded border ${currentPage === 1 ? 'text-slate-300 border-slate-100 cursor-not-allowed' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-slate-400">...</span>
                      )}
                      <button
                        onClick={() => paginate(page)}
                        className={`min-w-[32px] h-8 rounded text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))
                }

                <button
                  onClick={handleNextPage}
                  disabled={!canGoNext || loadingMore}
                  className={`p-2 rounded border ${(!canGoNext || loadingMore) ? 'text-slate-300 border-slate-100 cursor-not-allowed' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                >
                  {loadingMore ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      <ImageModal 
        isOpen={!!selectedItems} 
        onClose={() => {
          setSelectedItems(null);
          setSelectedImageUrl(null);
          setSelectedImageUrls(null);
        }}
        imageUrl={selectedImageUrl}
        imageUrls={selectedImageUrls}
        items={selectedItems}
      />
    </div>
  );
};

export default HistoryPage;
